// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useAuthContext } from '@tls/auth';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// the package also knows 'DEV' and 'QA', add them here once those IdP hosts are known
export type NoiseStage = 'DEV' | 'QA' | 'STAGING' | 'PROD';

// allowlist: an IdP session only yields tokens for the NOISE client of its own stage,
// and guessing PROD for an unknown authority would talk to the production gateway
const STAGE_BY_IDP_HOST: Record<string, NoiseStage> = {
  'account-s.trumpf.com': 'STAGING',
  'account.trumpf.com': 'PROD',
};

// null: not a known TRUMPF IdP, notifications are unavailable rather than pointed at prod
function stageFromAuthority(authority: string): NoiseStage | null {
  try {
    return STAGE_BY_IDP_HOST[new URL(authority).hostname] ?? null;
  } catch {
    return null;
  }
}

// uxn components expect java style locales
const NOISE_LOCALES: Record<string, string> = { de: 'de_DE', en: 'en_US' };

/** Stage and locale for the @trumpf/ux.notifications components, stage is undefined unless a known IdP resolved. */
export function useNoiseConfig() {
  const { i18n } = useTranslation();
  const { appBaseUrl } = useAuthContext();
  const language = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const metaUrl = `${appBaseUrl ?? ''}/api/meta`; // must stay the url @tls/auth loads

  const { data, isPending, error } = useQuery({
    queryKey: ['noise-stage', metaUrl],
    queryFn: async (): Promise<NoiseStage | null> => {
      const res = await fetch(metaUrl);
      if (!res.ok) throw new Error(`GET ${metaUrl} failed with ${res.status}`);
      const meta = (await res.json()) as { authority?: string };
      if (!meta.authority) throw new Error(`${metaUrl} returned no authority, cannot derive NOISE stage`);
      const stage = stageFromAuthority(meta.authority);
      if (!stage) console.warn(`No NOISE stage for authority ${meta.authority}, notifications stay disabled`);
      return stage;
    },
    staleTime: Infinity,
  });

  useEffect(() => {
    if (error) console.error('Cannot derive NOISE stage', error);
  }, [error]);

  return {
    stage: data ?? undefined,
    locale: NOISE_LOCALES[language] ?? 'en_US',
    isPending,
    /** No stage resolved; consumers should say so instead of rendering an empty shell. */
    isUnavailable: !isPending && (!!error || data === null),
  };
}
